import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { useQuery } from '@apollo/client';
import { matchPath, Route, Routes, useLocation } from 'react-router-dom';
import { Container, Stack, useMediaQuery, useTheme } from '@mui/material';
import { Breadcrumbs, BurgerMenu, Header, Main } from '../../components';
import { FindGrid, useFindResultsState } from '../../components/FindResults';
import { ViewModeToggle } from '../Site/SiteActionBar/ViewModeToggle';
import { GET_MY_FIND_GROUPS_QUERY, GET_MY_FINDS_QUERY } from '../../gql';
import { FindGroupDetail } from './FindGroupDetail';
import { FindGroupMenu } from './FindGroupMenu';
import { FindGroupsTable } from './FindGroupsTable';
import { FindsMenu } from './FindsMenu';
import { useIntl } from 'react-intl';

function AllFinds() {
  const state = useFindResultsState({ syncWithMap: true });
  const { data, loading } = useQuery(GET_MY_FINDS_QUERY, {
    variables: { filters: state.filter },
    fetchPolicy: 'cache-first',
  });

  return (
    <Main>
      <FindGrid
        rows={data?.getMyFinds ?? []}
        loading={loading}
        state={state}
        showCopyFromMap={false}
      />
    </Main>
  );
}

function FindGroups() {
  const { data, loading } = useQuery(GET_MY_FIND_GROUPS_QUERY, {
    fetchPolicy: 'cache-first',
  });

  return (
    <Main>
      <FindGroupsTable rows={data?.getMyFindGroups ?? []} loading={loading} />
    </Main>
  );
}

export const Finds = React.memo(() => {
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [findListMode, setFindListMode] = useState<'grid' | 'gallery'>('grid');
  const groupMatch = matchPath('/finds/groups/:groupId/*', location.pathname);
  const groupId = groupMatch?.params.groupId;
  const groupFindsPath = groupId ? `/finds/groups/${groupId}/finds` : undefined;
  const isGroupFinds = groupFindsPath === location.pathname;
  const menu = groupId ? <FindGroupMenu groupId={groupId} /> : <FindsMenu />;

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Helmet
        title={intl.formatMessage({
          id: 'finds.pageTitle',
          defaultMessage: 'Finds | Acolyte',
        })}
      />
      <Header mainIcon={isMobile && <BurgerMenu menu={menu} />} />
      <Container maxWidth="xl">
        <Stack direction="row">
          {!isMobile && menu}
          <Stack flexGrow={1} gap={1} minWidth={0}>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              alignItems={isMobile ? 'flex-start' : 'center'}
            >
              <Breadcrumbs />
              {isGroupFinds && (
                <ViewModeToggle
                  findListMode={findListMode}
                  setFindListMode={setFindListMode}
                />
              )}
            </Stack>
            <Routes>
              <Route index element={<AllFinds />} />
              <Route path="groups" element={<FindGroups />} />
              <Route
                path="groups/:groupId/*"
                element={<FindGroupDetail mode={findListMode} />}
              />
            </Routes>
          </Stack>
        </Stack>
      </Container>
    </div>
  );
});
